import { Link } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';

import type { WorkflowView } from '@/lib/api/generated/model';
import { useConnections } from '@/features/connections';
import { usePreferencesStore } from '@/shared/store/preferences';
import { firstRunSteps, type FirstRunStep } from '../firstRunSteps';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * A workspace that has not started yet, shown as the path to starting it
 * (spec 099 US2): three numbered steps with the action that does each and a
 * mark when it is done. Text and line-art only — the constitution rules out an
 * illustrated empty state, and this needs none: the content really is a
 * sequence, so the numbers carry the meaning.
 */
function FirstRunStateControl({
  workspaceId,
  workflows,
  taskCount,
  compact = false,
}: {
  workspaceId: string;
  workflows: readonly WorkflowView[];
  taskCount: number;
  /**
   * One line above an ordinary page (spec 128 FR-011): a set-up workspace
   * with no task yet still has a next step, but spec 099 found a full
   * onboarding panel over it read as a regression.
   */
  compact?: boolean;
}) {
  // An enabled GitHub or GitLab connection ticks the optional step; while the
  // list is unknown (still reading, or not readable) the step is left out
  // rather than shown wrong (spec 128 FR-012).
  const { connections } = useConnections(workspaceId);
  const hasCodeHost =
    connections === undefined
      ? undefined
      : connections.some(
          (c) => c.enabled && (c.type === 'github' || c.type === 'gitlab'),
        );
  const steps = firstRunSteps(workflows, taskCount, hasCodeHost);
  const dismiss = usePreferencesStore((state) => state.dismissSetup);
  const nextKey = steps.find((step) => !step.done)?.key;
  const next = steps.find((step) => !step.done && !step.optional);
  if (compact) {
    if (!next) return null;
    const required = steps.filter((step) => !step.optional);
    return (
      <section
        aria-label="Setup"
        className="border-border flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-dashed px-4 py-2.5 text-sm"
      >
        <span className="text-muted-foreground">
          {required.filter((step) => step.done).length} of {required.length}{' '}
          setup steps done · Next: {next.title.toLowerCase()}
        </span>
        <StepLink workspaceId={workspaceId} step={next} emphasis />
        <button
          type="button"
          onClick={() => {
            dismiss(workspaceId);
          }}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring ms-auto rounded-sm text-xs underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Dismiss
        </button>
      </section>
    );
  }
  return (
    <section
      aria-labelledby="first-run-heading"
      className="border-border flex flex-col gap-5 rounded-lg border border-dashed p-6 sm:p-8"
    >
      <div className="relative flex flex-col gap-1 pe-20">
        <button
          type="button"
          onClick={() => {
            dismiss(workspaceId);
          }}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute end-0 top-0 rounded-sm text-xs underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Dismiss
        </button>
        <h2
          id="first-run-heading"
          className="text-foreground text-xl font-medium"
        >
          Steps to the first run
        </h2>
        <p className="text-muted-foreground max-w-prose text-sm">
          A workspace does its work through workflows. Once one is published,
          its board appears here with everything running on it.
        </p>
      </div>
      <ol className="flex flex-col gap-4">
        {steps.map((step, index) => (
          <li key={step.key} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={`flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums ${
                step.done
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground'
              }`}
            >
              {step.done ? (
                <CheckIcon className="size-3.5" strokeWidth={2.5} />
              ) : (
                index + 1
              )}
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="text-foreground text-sm font-medium">
                {step.title}
                {step.done ? (
                  <span className="text-muted-foreground font-normal">
                    {' '}
                    · done
                  </span>
                ) : step.optional ? (
                  <span className="text-muted-foreground font-normal">
                    {' '}
                    · optional
                  </span>
                ) : null}
              </p>
              <p className="text-muted-foreground text-sm">{step.detail}</p>
              {!step.done ? (
                <StepLink
                  workspaceId={workspaceId}
                  step={step}
                  emphasis={step.key === nextKey}
                />
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function StepLink({
  workspaceId,
  step,
  emphasis,
}: {
  workspaceId: string;
  step: FirstRunStep;
  emphasis: boolean;
}) {
  const className = `mt-1 w-fit rounded-sm text-sm underline underline-offset-4 ${
    emphasis ? 'text-primary font-medium' : 'text-foreground'
  }`;
  switch (step.target.kind) {
    case 'builder':
      return (
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
          params={{ workspaceId, workflowKey: step.target.workflowKey }}
          className={className}
        >
          Open the builder
        </Link>
      );
    case 'board':
      return (
        <Link
          to="/workspaces/$workspaceId/workflows/$workflowKey/board"
          params={{ workspaceId, workflowKey: step.target.workflowKey }}
          className={className}
        >
          Open the board
        </Link>
      );
    case 'library':
      return (
        <Link
          to="/workspaces/$workspaceId/library"
          params={{ workspaceId }}
          className={className}
        >
          Add one from the Library
        </Link>
      );
    case 'connection':
      return (
        <Link
          to="/workspaces/$workspaceId/integrations/connections"
          params={{ workspaceId }}
          search={{ create: true }}
          className={className}
        >
          Add a connection
        </Link>
      );
    default:
      return (
        <Link
          to="/workspaces/$workspaceId/workflows"
          params={{ workspaceId }}
          className={className}
        >
          Go to workflows
        </Link>
      );
  }
}

/** Setting the workspace up is for its members; someone only reading it is not asked to (spec 189). */
export function FirstRunState(
  props: Parameters<typeof FirstRunStateControl>[0],
) {
  return useCanWork() ? <FirstRunStateControl {...props} /> : null;
}
